import { Flex, Group, Link } from '@web/components/atoms/Layout'
import { type SocialShare as SocialShareComponentProps } from '@web/graphql-components'
import { IconButton } from '@web/components/atoms/Button'
import { useColorWithOpacity } from '@web/components/molecules/ColorPicker/utils'
import {
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverRoot,
  PopoverTrigger,
} from '@web/components/atoms/Popover'
import { Box } from '@web/components/atoms/Layout'
import { PageButton } from '../PageButton'
import { Body2 } from '@web/components/atoms/Typography'
import { Input } from '@web/components/atoms/Input'
import { useEffect, useRef, useState } from 'react'
import { MOBILE_CONTAINER_QUERY, DESKTOP_CONTAINER_QUERY } from '../utils'
import { useIsMobile } from '@web/components/utils/chakra-hooks'

const LinkIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M5.89376 8.1775L4.17876 9.8925C3.47544 10.5958 3.08032 11.5497 3.08032 12.5444C3.08032 13.539 3.47544 14.4929 4.17876 15.1962C4.88209 15.8996 5.83599 16.2947 6.83064 16.2947C7.82529 16.2947 8.77919 15.8996 9.48251 15.1962L11.7675 12.91C12.1916 12.4858 12.5078 11.9661 12.6895 11.3944C12.8712 10.8228 12.9131 10.2159 12.8117 9.62467C12.7103 9.03346 12.4686 8.47521 12.1068 7.99676C11.745 7.51831 11.2737 7.13362 10.7325 6.875L10 7.6075C9.92565 7.682 9.86097 7.76557 9.80751 7.85625C10.2257 7.97646 10.6052 8.20372 10.9086 8.51557C11.212 8.82742 11.4288 9.21305 11.5375 9.63435C11.6462 10.0556 11.6431 10.498 11.5284 10.9177C11.4138 11.3375 11.1916 11.72 10.8838 12.0275L8.60001 14.3125C8.13091 14.7816 7.49467 15.0451 6.83126 15.0451C6.16785 15.0451 5.53162 14.7816 5.06251 14.3125C4.59341 13.8434 4.32987 13.2072 4.32987 12.5437C4.32987 11.8803 4.59341 11.2441 5.06251 10.775L6.05376 9.785C5.91391 9.26091 5.85988 8.71762 5.89376 8.17625V8.1775Z"
      fill="currentColor"
    />
    <path
      d="M8.23244 5.84C7.80832 6.26418 7.49214 6.78391 7.31045 7.35557C7.12876 7.92722 7.08685 8.53413 7.18824 9.12533C7.28963 9.71654 7.53136 10.2748 7.89315 10.7532C8.25494 11.2317 8.72622 11.6164 9.26744 11.875L10.2362 10.905C9.81237 10.7913 9.42594 10.5681 9.11573 10.2578C8.80552 9.94741 8.58246 9.56089 8.46895 9.13703C8.35545 8.71317 8.35551 8.26689 8.46912 7.84306C8.58273 7.41923 8.8059 7.03276 9.11619 6.7225L11.3999 4.4375C11.869 3.9684 12.5053 3.70486 13.1687 3.70486C13.8321 3.70486 14.4683 3.9684 14.9374 4.4375C15.4065 4.9066 15.6701 5.54284 15.6701 6.20625C15.6701 6.86966 15.4065 7.5059 14.9374 7.975L13.9462 8.965C14.0862 9.49 14.1399 10.0338 14.1062 10.5738L15.8212 8.85875C16.5245 8.15543 16.9196 7.20152 16.9196 6.20688C16.9196 5.21223 16.5245 4.25832 15.8212 3.555C15.1179 2.85168 14.164 2.45656 13.1693 2.45656C12.1747 2.45656 11.2208 2.85168 10.5174 3.555L8.23244 5.84Z"
      fill="currentColor"
    />
  </svg>
)

const EmailIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M0 4C0 3.46957 0.210714 2.96086 0.585786 2.58579C0.960859 2.21071 1.46957 2 2 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V12C16 12.5304 15.7893 13.0391 15.4142 13.4142C15.0391 13.7893 14.5304 14 14 14H2C1.46957 14 0.960859 13.7893 0.585786 13.4142C0.210714 13.0391 0 12.5304 0 12V4ZM2 3C1.73478 3 1.48043 3.10536 1.29289 3.29289C1.10536 3.48043 1 3.73478 1 4V4.217L8 8.417L15 4.217V4C15 3.73478 14.8946 3.48043 14.7071 3.29289C14.5196 3.10536 14.2652 3 14 3H2ZM15 5.383L10.242 8.238L15 11.114V5.384V5.383ZM14.966 12.261L9.271 8.82L8 9.583L6.728 8.82L1.034 12.26C1.0912 12.4724 1.21685 12.6601 1.39149 12.7938C1.56613 12.9276 1.78001 13.0001 2 13H14C14.2199 13.0001 14.4336 12.9278 14.6083 12.7942C14.7829 12.6607 14.9086 12.4732 14.966 12.261ZM1 11.114L5.758 8.238L1 5.383V11.113V11.114Z"
      fill="currentColor"
    />
  </svg>
)

const FacebookIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M16 8C16 3.6 12.4 0 8 0C3.6 0 0 3.6 0 8C0 12 2.9 15.3 6.7 15.9V10.3H4.7V8H6.7V6.2C6.7 4.2 7.9 3.1 9.7 3.1C10.6 3.1 11.5 3.3 11.5 3.3V5.3H10.5C9.5 5.3 9.2 5.9 9.2 6.5V8H11.4L11 10.3H9.1V16C13.1 15.4 16 12 16 8Z"
      fill="currentColor"
    />
  </svg>
)

const TwitterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 1200 1227" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M714.163 519.284L1160.89 0H1055.03L667.137 450.887L357.328 0H0L468.492 681.821L0 1226.37H105.866L515.491 750.218L842.672 1226.37H1200L714.137 519.284H714.163ZM569.165 687.828L521.697 619.934L144.011 79.6944H306.615L611.412 515.685L658.88 583.579L1055.08 1150.3H892.476L569.165 687.854V687.828Z"
      fill="currentColor"
    />
  </svg>
)

const LinkedInIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3.60001 16H0.199997V5.3H3.60001V16ZM1.9 3.8C0.800002 3.8 0 3 0 1.9C0 0.8 0.900002 0 1.9 0C3 0 3.8 0.8 3.8 1.9C3.8 3 3 3.8 1.9 3.8ZM16 16H12.6V10.2C12.6 8.5 11.9 8 10.9 8C9.89999 8 8.89999 8.8 8.89999 10.3V16H5.5V5.3H8.7V6.8C9 6.1 10.2 5 11.9 5C13.8 5 15.8 6.1 15.8 9.4V16H16Z"
      fill="currentColor"
    />
  </svg>
)

const WhatsAppIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M13.6 2.3C12.1 0.8 10.1 0 8 0C3.6 0 0 3.6 0 8C0 9.4 0.400006 10.8 1.10001 12L0 16L4.20001 14.9C5.40001 15.5 6.7 15.9 8 15.9C12.4 15.9 16 12.3 16 7.9C16 5.8 15.1 3.8 13.6 2.3ZM8 14.6C6.8 14.6 5.60001 14.3 4.60001 13.7L4.39999 13.6L1.89999 14.3L2.60001 11.9L2.39999 11.6C1.69999 10.5 1.39999 9.3 1.39999 8.1C1.39999 4.5 4.4 1.5 8 1.5C9.8 1.5 11.4 2.2 12.7 3.4C14 4.7 14.6 6.3 14.6 8.1C14.6 11.6 11.7 14.6 8 14.6ZM11.6 9.6C11.4 9.5 10.4 9 10.2 9C10 8.9 9.89999 8.9 9.79999 9.1C9.69999 9.3 9.30001 9.7 9.20001 9.9C9.10001 10 8.99999 10 8.79999 10C8.59999 9.9 8.00001 9.7 7.20001 9C6.60001 8.5 6.20001 7.8 6.10001 7.6C6.00001 7.4 6.10001 7.3 6.20001 7.2C6.30001 7.1 6.4 7 6.5 6.9C6.6 6.8 6.60001 6.7 6.70001 6.6C6.80001 6.5 6.70001 6.4 6.70001 6.3C6.70001 6.2 6.30001 5.2 6.10001 4.8C6.00001 4.5 5.80001 4.5 5.70001 4.5C5.60001 4.5 5.49999 4.5 5.29999 4.5C5.19999 4.5 4.99999 4.5 4.79999 4.7C4.59999 4.9 4.10001 5.4 4.10001 6.4C4.10001 7.4 4.79999 8.3 4.89999 8.5C4.99999 8.6 6.29999 10.7 8.29999 11.5C9.99999 12.2 10.3 12 10.7 12C11.1 12 11.9 11.5 12 11.1C12.2 10.6 12.2 10.2 12.1 10.2C12 9.7 11.8 9.7 11.6 9.6Z"
      fill="currentColor"
    />
  </svg>
)

type LinkIconButtonProps = {
  label: string
  href: string
  icon: React.ReactElement
  defaultColor: string
  onClick?: () => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  styles?: any
}

const LinkIconButton = ({ label, href, icon, styles, onClick, defaultColor }: LinkIconButtonProps) => {
  const bgColor = styles?.useDefaultColors === 'true' ? defaultColor : `var(--branding-button-primary-bg-color)`
  const hoverBgColor =
    styles?.useDefaultColors === 'true' ? defaultColor : `var(--branding-button-primary-hover-bg-color)`
  const backgroundColor = useColorWithOpacity(
    styles?.buttonBackgroundColor,
    styles?.buttonBackgroundColorOpacity,
    bgColor,
  )
  const hoverBackgroundColor = useColorWithOpacity(
    styles?.hoverBackgroundColor,
    styles?.hoverBackgroundColorOpacity,
    hoverBgColor,
  )
  const iconColor = useColorWithOpacity(styles?.iconColor, styles?.iconColorOpacity, '#FFFFFF')

  const buttonStyles = {
    bgColor: backgroundColor,
    color: iconColor,
    _hover: {
      bgColor: hoverBackgroundColor,
    },
  }

  if (onClick) {
    return (
      <IconButton
        aria-label="Copy link"
        onClick={onClick}
        buttonAppearance="unstyled"
        borderRadius="50%"
        {...buttonStyles}
      >
        {icon}
      </IconButton>
    )
  }

  return (
    <Link href={href} target="_blank" rel="noreferrer">
      <IconButton aria-label={`share-to-${label}`} buttonAppearance="unstyled" borderRadius="50%" {...buttonStyles}>
        {icon}
      </IconButton>
    </Link>
  )
}

type SocialShareProps = SocialShareComponentProps & { url?: string }

const SocialShareButtons = ({
  hasLink,
  hasEmail,
  hasFacebook,
  hasLinkedIn,
  hasTwitter,
  hasWhatsApp,
  styles,
  url = '',
}: Partial<SocialShareProps>) => {
  const encodedUrl = encodeURIComponent(url)
  const isMobile = useIsMobile()
  return (
    <Flex gap="4px" justifyContent={styles?.alignSelf || 'center'}>
      {hasLink && (
        <Popover
          theme="dark"
          triggerEl={
            <Box>
              <LinkIconButton
                href=""
                label="copy-link"
                icon={<LinkIcon />}
                styles={styles}
                defaultColor="#D65C4C"
                onClick={() => navigator.clipboard.writeText(url)}
              />
            </Box>
          }
        >
          Link copied!
        </Popover>
      )}
      {hasEmail && (
        <LinkIconButton
          label="Email"
          href={`mailto:email@toshare.com?subject=Check%20this%20out&body=Check%20this%20link:%20${encodedUrl}`}
          icon={<EmailIcon />}
          styles={styles}
          defaultColor="#0F6CBD"
        />
      )}
      {hasFacebook && (
        <LinkIconButton
          label="Facebook"
          {...(isMobile
            ? {
                onClick: () => {
                  navigator.share
                    ? navigator.share({
                        url,
                      })
                    : window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, '_blank')
                },
              }
            : {})}
          href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
          icon={<FacebookIcon />}
          styles={styles}
          defaultColor="#1877F2"
        />
      )}
      {hasTwitter && (
        <LinkIconButton
          label="X"
          href={`https://twitter.com/intent/tweet?url=${encodedUrl}`}
          icon={<TwitterIcon />}
          styles={styles}
          defaultColor="#000000"
        />
      )}
      {hasLinkedIn && (
        <LinkIconButton
          label="LinkedIn"
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
          icon={<LinkedInIcon />}
          styles={styles}
          defaultColor="#0077B5"
        />
      )}
      {hasWhatsApp && (
        <LinkIconButton
          label="WhatsApp"
          href={`https://api.whatsapp.com/send?text=${encodedUrl}`}
          icon={<WhatsAppIcon />}
          styles={styles}
          defaultColor="#25D366"
        />
      )}
    </Flex>
  )
}

const ShareIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M2.66673 8.99999C3.25013 8.99668 3.81125 8.77556 4.24007 8.37999L8.4134 10.7667C8.36024 10.9508 8.33331 11.1416 8.3334 11.3333C8.32912 11.8744 8.51139 12.4003 8.84952 12.8227C9.18766 13.2451 9.661 13.538 10.1899 13.6523C10.7187 13.7665 11.2708 13.695 11.7531 13.4498C12.2354 13.2046 12.6185 12.8008 12.8379 12.3062C13.0572 11.8116 13.0995 11.2565 12.9575 10.7345C12.8156 10.2124 12.498 9.75515 12.0584 9.43977C11.6188 9.1244 11.0839 8.97013 10.5439 9.00296C10.0038 9.03578 9.49159 9.25369 9.0934 9.61999L4.92007 7.23333C4.97043 7.06904 4.99737 6.89847 5.00007 6.72666L9.10007 4.37999C9.49611 4.73789 10.0032 4.94872 10.5362 4.97713C11.0692 5.00554 11.5958 4.8498 12.0277 4.53603C12.4595 4.22226 12.7703 3.76954 12.908 3.25381C13.0457 2.73807 13.0018 2.19068 12.7838 1.70344C12.5658 1.21621 12.1868 0.818754 11.7105 0.577744C11.2342 0.336735 10.6896 0.266826 10.1679 0.379743C9.64614 0.49266 9.17912 0.781534 8.84512 1.19792C8.51111 1.6143 8.33044 2.13287 8.3334 2.66666C8.33566 2.8582 8.36254 3.04865 8.4134 3.23333L4.62007 5.39999C4.40027 5.05996 4.09578 4.78298 3.73652 4.59626C3.37727 4.40955 2.9756 4.31953 2.57102 4.33505C2.16643 4.35057 1.77285 4.47111 1.42896 4.6848C1.08506 4.8985 0.80269 5.198 0.609596 5.55387C0.416503 5.90974 0.319334 6.30974 0.32764 6.71453C0.335945 7.11933 0.44944 7.515 0.656968 7.86265C0.864497 8.2103 1.15892 8.49797 1.51129 8.69738C1.86366 8.89679 2.26185 9.00108 2.66673 8.99999ZM10.6667 10.3333C10.8645 10.3333 11.0579 10.392 11.2223 10.5019C11.3868 10.6117 11.5149 10.7679 11.5906 10.9506C11.6663 11.1334 11.6861 11.3344 11.6475 11.5284C11.6089 11.7224 11.5137 11.9006 11.3738 12.0404C11.234 12.1803 11.0558 12.2755 10.8618 12.3141C10.6678 12.3527 10.4668 12.3329 10.2841 12.2572C10.1013 12.1815 9.94515 12.0533 9.83527 11.8889C9.72538 11.7244 9.66673 11.5311 9.66673 11.3333C9.66673 11.0681 9.77209 10.8138 9.95963 10.6262C10.1472 10.4387 10.4015 10.3333 10.6667 10.3333ZM10.6667 1.66666C10.8645 1.66666 11.0579 1.72531 11.2223 1.83519C11.3868 1.94507 11.5149 2.10125 11.5906 2.28398C11.6663 2.4667 11.6861 2.66777 11.6475 2.86175C11.6089 3.05573 11.5137 3.23391 11.3738 3.37377C11.234 3.51362 11.0558 3.60886 10.8618 3.64744C10.6678 3.68603 10.4668 3.66623 10.2841 3.59054C10.1013 3.51485 9.94515 3.38668 9.83527 3.22223C9.72538 3.05778 9.66673 2.86444 9.66673 2.66666C9.66673 2.40144 9.77209 2.14709 9.95963 1.95955C10.1472 1.77202 10.4015 1.66666 10.6667 1.66666ZM2.66673 5.66666C2.86452 5.66666 3.05786 5.72531 3.22231 5.83519C3.38675 5.94507 3.51493 6.10125 3.59061 6.28398C3.6663 6.4667 3.68611 6.66777 3.64752 6.86175C3.60894 7.05573 3.51369 7.23391 3.37384 7.37377C3.23399 7.51362 3.05581 7.60886 2.86183 7.64744C2.66784 7.68603 2.46678 7.66623 2.28405 7.59054C2.10133 7.51485 1.94515 7.38668 1.83527 7.22223C1.72538 7.05778 1.66673 6.86444 1.66673 6.66666C1.66673 6.40144 1.77209 6.14709 1.95963 5.95955C2.14716 5.77202 2.40152 5.66666 2.66673 5.66666Z"
      fill="currentColor"
    />
  </svg>
)

const SocialShare = (props: SocialShareProps & { isFullWidth?: boolean }) => {
  const [url, setUrl] = useState(props.url ?? '')
  const { isFullWidth = true } = props
  const shareContainerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined' || props.url) {
      return
    }

    setUrl(window.location.href)
  }, [])

  return (
    <Box
      css={{
        [MOBILE_CONTAINER_QUERY]: {
          p: props.mobileStyles?.padding ?? 0,
        },
        [DESKTOP_CONTAINER_QUERY]: {
          p: props.styles?.padding ?? 0,
        },
      }}
    >
      {props.styles?.layoutDisplay === 'dropdown' ? (
        <PopoverRoot>
          <PopoverTrigger asChild>
            <Box ref={shareContainerRef}>
              <Box
                css={{
                  display: isFullWidth ? 'block' : 'none',
                  '@container page (min-width: 901px)': {
                    display: 'block',
                  },
                }}
              >
                <PageButton
                  name={props.styles?.buttonType || 'Primary'}
                  iconSlot={<ShareIcon />}
                  buttonText="Share"
                  width="100%"
                />
              </Box>
              {!isFullWidth && (
                <Box
                  css={{
                    display: 'block',
                    '@container page (min-width: 901px)': {
                      display: 'none',
                    },
                  }}
                >
                  <PageButton name={props.styles?.buttonType || 'Primary'} iconSlot={<ShareIcon />} />
                </Box>
              )}
            </Box>
          </PopoverTrigger>
          <PopoverContent portalRef={shareContainerRef}>
            <PopoverArrow />
            <PopoverBody p={0}>
              <Box
                bgColor="#FFF"
                borderRadius="8px"
                boxShadow="0px 2px 10px rgba(0, 0, 0, 0.1)"
                border="1px solid #E8E8E8"
                overflow="hidden"
              >
                {props.hasLink && (
                  <Box w="100%" px={4} py={2} bgColor="ge.background.primary" pr="40px">
                    <Group attached={true}>
                      <Input id="social-share-input" readOnly={true} value={url} />
                      <Popover
                        theme="dark"
                        hasCloseButton={false}
                        positioning={{
                          placement: 'top',
                        }}
                        triggerEl={
                          <Box>
                            <PageButton
                              name="Primary"
                              buttonText="Copy Link"
                              size="xs"
                              onClick={() => navigator.clipboard.writeText(url)}
                              width="100%"
                              styleOverride={{
                                minWidth: '80px',
                                height: '39px',
                                fontSize: '14px',
                                borderBottomLeftRadius: '0px !important',
                                borderTopLeftRadius: '0px !important',
                                borderBottomRightRadius: '6px !important',
                                borderTopRightRadius: '6px !important',
                              }}
                            />
                          </Box>
                        }
                      >
                        <Box p={2}>Link copied!</Box>
                      </Popover>
                    </Group>
                  </Box>
                )}
                <Box px={4} py={2}>
                  <Body2 mb={2} w="fit-content">
                    Share to socials
                  </Body2>
                  <SocialShareButtons {...props} />
                </Box>
              </Box>
            </PopoverBody>
          </PopoverContent>
        </PopoverRoot>
      ) : (
        <SocialShareButtons {...props} />
      )}
    </Box>
  )
}

export default SocialShare
